import {
  ConfigurationTheme,
  ConfigurationThemeResponse,
  TextAlignment,
} from '@tink/config/src/tinkLinkConfig';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ApplicationError } from '../components/ApplicationErrorBoundary/errors/ApplicationError';
import { ajaxRetry } from '../lib-api/ajaxRetry';
import { base, createApiURL, ResponseObj } from '../lib-api/services/base';

export const ERR_FAILED_TO_FETCH_THEMES = {
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_THEMES,
  errorStatus: ErrorStatus.INTERNAL_ERROR,
};

export const ERR_FAILED_TO_FETCH_MERCHANT_THEMES = {
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_MERCHANT_THEMES,
  errorStatus: ErrorStatus.INTERNAL_ERROR,
};

type ThemeResponse = {
  themes: ConfigurationThemeResponse[];
};

export type ThemeClient = {
  getAppThemes: () => Promise<ResponseObj<ThemeResponse>>;
  getThemesByMerchantId: (merchantId?: string) => Promise<ResponseObj<ThemeResponse>>;
};

const themeClient: ThemeClient = {
  getAppThemes: () =>
    ajaxRetry({
      headers: base.headers(),
      url: createApiURL('/link/v1/apps/themes'),
    }),
  getThemesByMerchantId: (merchantId) =>
    ajaxRetry({
      headers: base.headers(),

      url: createApiURL(`/link/v1/merchant/internal/${merchantId}/themes`),
    }),
};

export type ThemeService = {
  getAppThemes: () => Promise<ConfigurationTheme[]>;
  getThemesByMerchantId: (merchantId: string) => Promise<ConfigurationTheme[]>;
};

export const applyThemeDefaults = (themeResponse: ThemeResponse): ConfigurationTheme[] => {
  return themeResponse.themes.map((theme) => ({
    ...theme,
    textAlignment: TextAlignment.LEFT,
  }));
};

export const createThemeService = (client: ThemeClient): ThemeService => ({
  getAppThemes: async () => {
    try {
      const themeResponse = await client.getAppThemes();

      if (!themeResponse.isOk()) {
        throw new ApplicationError(ERR_FAILED_TO_FETCH_THEMES);
      }

      if (themeResponse.isOk() && themeResponse.data.themes) {
        return applyThemeDefaults(themeResponse.data);
      }

      return [];
    } catch (error) {
      throw new ApplicationError(ERR_FAILED_TO_FETCH_THEMES);
    }
  },
  getThemesByMerchantId: async (merchantId) => {
    try {
      const themeResponse = await client.getThemesByMerchantId(merchantId);

      if (!themeResponse.isOk()) {
        throw new ApplicationError(ERR_FAILED_TO_FETCH_MERCHANT_THEMES);
      }

      if (themeResponse.isOk() && themeResponse.data.themes) {
        return applyThemeDefaults(themeResponse.data);
      }

      return [];
    } catch (error) {
      throw new ApplicationError(ERR_FAILED_TO_FETCH_MERCHANT_THEMES);
    }
  },
});

export const themeService = createThemeService(themeClient);
